import React, { useEffect, useState } from "react";

import { checkValidUrl } from "@/Utils";

import { EColors } from "@atoms/ZColors/interface";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import ZImage from "@atoms/ZImage";
import ZInput from "../../ZInput";
import { EFontWeights } from "@atoms/ZTypography/interface";
import ZTypography from "@atoms/ZTypography";
import { DragDrop } from "@uppy/react";

import Col from "antd/lib/col";
import Row from "antd/lib/row";

import { UploadStage } from "../interface";
import { IRenderStageContent } from "./interface";
import styles from "../index.module.less";
import { getYoutubeID } from "@/Contracts";

const ZRenderStageContent = ({
	linkState,
	finishedState,
	progressState,
	...props
}: IRenderStageContent) => {
	const [isImageError, setIsImageError] = useState<boolean>(false);
	const [isVideoError, setIsVideoError] = useState<boolean>(false);

	useEffect(() => {
		setIsImageError(false);
		setIsVideoError(false);
	}, [linkState.linkValue]);

	const { ZText, ZSectionSubheading } = ZTypography;

	const uploadFinished = (
		<div
			className={`flex flex-col justify-center items-center  w-full h-full`}
		>
			<ZIcon
				size={"xl"}
				name={EIcons.CHECKCIRCLE}
				color={EColors.GREEN}
				colorVariant={7}
			/>

			<ZText level={2} className={`p-3`} weight={EFontWeights.MEDIUM}>
				{finishedState.uploadSuccessMessage ??
					"File uploaded successfully"}
			</ZText>

			<div className={`p-4 flex justify-center items-center`}>
				<ZIcon name={EIcons.FILE} colorVariant={7} />
				<ZText className={`px-2`}>
					{finishedState.file?.name.slice(0, 15) +
						"..." +
						finishedState.file?.name.slice(
							finishedState.file?.name.length - 15
						)}
				</ZText>
			</div>
		</div>
	);

	const getTextSubHeading = (): string => {
		const {
			showFileMetaInfoText,
			isImage,
			isVideo,
			isPDF,
			isCSV,
			isDOCX,
			isTXT,
			isPPTX,
			isXLSX
		} = linkState;
		if (!showFileMetaInfoText) return "";
		if (
			isImage &&
			isVideo &&
			isPDF &&
			isCSV &&
			isDOCX &&
			isTXT &&
			isPPTX &&
			isXLSX
		) {
			return "Max file size: 100MB. Supports: jpg, jpeg, png, mp4, pdf, csv, txt, doc/docx, ppt/pptx, xls/xlsx";
		} else if (isImage && isVideo && isPDF) {
			return "Max file size: 25MB. Supports: jpg, jpeg, png, mp4, pdf";
		} else if (isImage) {
			if (props.maxFileSize && props?.maxFileSize <= 2048000)
				return "Max. Image size: 2MB & Supports: jpg, jpeg & png";
			return "Max. Image size: 25MB & Supports: jpg, jpeg & png";
		} else if (isVideo) {
			return "Max. Video size: 500MB & Supports: mp4, mkv, & webm";
		} else if (isPDF && isDOCX && isTXT) {
			return "Only pdf, txt and doc/docx file types are supported";
		} else if (isPDF) {
			return "Zomentum only supports PDF file format";
		} else if (isCSV && isXLSX) {
			return "Only Excel (.xls and .xlsx) and .csv file types are supported.";
		} else {
			return "";
		}
	};

	const { linkValue, isImage, isVideo, isAll } = linkState;
	switch (props.stage) {
		case UploadStage.FILE_SELECT: {
			return (
				<>
					<div className={`absolute w-full h-full`}>
						<DragDrop
							uppy={props.uppy}
							locale={{
								strings: {
									// Text to show on the droppable area.
									// `%{browse}` is replaced with a link that opens the system file selection dialog.
									dropHereOr: "%{browse}",
									// Used as the label for the link that opens the system file selection dialog.
									browse: `Or Browse ${
										isImage ? "images" : "files"
									}`
								}
							}}
						/>
					</div>
					<div
						className={` w-full h-full flex flex-col justify-start items-center pt-10 ${styles.zUploadModalWrapper}`}
					>
						<ZText
							level={2}
							className={`p-3`}
							weight={EFontWeights.MEDIUM}
						>{`Drag & drop your ${
							isImage ? "image" : "file"
						} here`}</ZText>
						<ZIcon
							name={EIcons.UPLOADCLOUDV2}
							size={"xl"}
							color={EColors.GRAY}
							colorVariant={6}
							fill
						/>
						<div className={`h-1/5`}></div>
						<ZSectionSubheading
							className="mt-6"
							colorVariant={8}
							weight={EFontWeights.MEDIUM}
						>
							{getTextSubHeading()}
						</ZSectionSubheading>
					</div>
				</>
			);
		}

		case UploadStage.UPLOAD_LINK: {
			return (
				<div className="flex flex-col mt-4 mx-4">
					<ZText weight={EFontWeights.MEDIUM}>
						{isImage
							? "Insert image link"
							: "Insert file link to import resource"}
					</ZText>

					<Row className="mt-4">
						<Col span={24}>
							<ZInput
								inputType="normal"
								value={linkValue}
								placeholder="Enter your link here"
								width="full"
								onClick={e => e.stopPropagation()}
								onChange={linkState.handleInputLinkChange}
							/>
							<div className="flex flex-col">
								{linkValue !== "" &&
									checkValidUrl(linkValue) && (
										<>
											<ZText
												className="my-2"
												colorVariant={8}
											>
												Preview
											</ZText>

											{(isImage || isAll) &&
												!isImageError && (
													<ZImage
														preview={false}
														src={linkValue}
														className="object-contain"
														onError={() =>
															setIsImageError(
																true
															)
														}
													/>
												)}

											{(isVideo || isAll) &&
												(linkState.isYoutubeVideoLink ? (
													<div
														className={`${styles.videoStyles} overflow-hidden flex items-center`}
													>
														<iframe
															title="Youtube Video"
															width="100%"
															height="auto"
															src={`https://www.youtube.com/embed/${getYoutubeID(
																linkValue
															)}?controls=0&showinfo=0&autohide=1`}
															allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
														></iframe>
													</div>
												) : (
													!isVideoError &&
													isImageError && (
														<video
															controls
															className={
																styles.videoStyles
															}
														>
															<source
																onError={() =>
																	setIsVideoError(
																		true
																	)
																}
																src={linkValue}
															></source>
														</video>
													)
												))}

											{isImageError && isVideoError && (
												<ZText
													level={0}
													colorVariant={6}
													className="h-34 flex items-center justify-center border border-solid border-gray-6 bg-gray-2 rounded"
												>
													Preview not available
												</ZText>
											)}
										</>
									)}
							</div>
						</Col>
					</Row>
				</div>
			);
		}

		case UploadStage.UPLOADING_DONE:
			return uploadFinished;

		case UploadStage.UPLOADING_IN_PROGRESS: {
			return (
				<div
					className={`flex flex-col justify-center items-center  w-full h-full`}
				>
					<ZText
						level={2}
						className={`p-3`}
						weight={EFontWeights.MEDIUM}
					>
						{progressState.uploadingText ?? "Uploading file"}
					</ZText>
					<div className={`w-2/3 h-1 0 bg-gray-6 mb-8`}>
						<div
							className={`h-full delay-20 bg-blue-6`}
							style={{
								width: `${progressState.loadingPercentage}%`
							}}
						></div>
					</div>
				</div>
			);
		}
	}
};

export default ZRenderStageContent;
